// frontend/CompletedSessions.js

import React, { useState, useEffect } from 'react';
import { Button, Box, Typography, CircularProgress, Alert, Grid, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const CompletedSessions = () => {
    const [loading, setLoading] = useState(true);
    const [sessions, setSessions] = useState([]);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedSessionId, setSelectedSessionId] = useState(null);

    // Fetch all completed sessions from the backend
    useEffect(() => {
        axios.get(`${API_URL}/completed-sessions/`)
            .then(response => {
                setSessions(response.data);
                setLoading(false);
            })
            .catch(err => {
                setError("Error fetching completed sessions.");
                setLoading(false);
            });
    }, []);

    // Handle Payment Completed button click - Open dialog
    const handlePaymentCompletedClick = (sessionId) => {
        setSelectedSessionId(sessionId);
        setOpenDialog(true);
    };

    // Handle dialog close
    const handleDialogClose = () => {
        setOpenDialog(false);
        setSelectedSessionId(null);
    };

    // Confirm Payment Completed status change
    const handleConfirmPayment = () => {
        if (!selectedSessionId) return;

        setLoading(true);
        axios.post(`${API_URL}/update-payment-status/`, { session_id: selectedSessionId })
            .then(response => {
                // After successful update, update the session status locally
                setSessions(sessions.map(session =>
                    session.session_id === selectedSessionId ? { ...session, status: 'payment completed' } : session
                ));
                setLoading(false);
                handleDialogClose(); // Close the dialog after confirmation
            })
            .catch(err => {
                setError("Error updating payment status.");
                setLoading(false);
                handleDialogClose(); // Close the dialog if error occurs
            });
    };

    // Filter sessions to show only those with status 'pending'
    const pendingSessions = sessions.filter(session => session.status === 'pending');

    return (
        <Box sx={{ padding: 3, backgroundColor: '#f5f5f5' }}>
            <Typography variant="h4" component="h1" sx={{ marginBottom: 3 }}>Completed Sessions (Pending Status)</Typography>

            {loading && <CircularProgress />}

            {error && <Alert severity="error">{error}</Alert>}

            {!loading && !error && pendingSessions.length === 0 && (
                <Typography>No pending sessions found.</Typography>
            )}

            {!loading && !error && pendingSessions.length > 0 && (
                <Grid container spacing={3}>
                    {pendingSessions.map(session => (
                        <Grid item xs={12} sm={6} md={4} key={session.session_id}>
                            <Box
                                sx={{
                                    backgroundColor: '#fff',
                                    padding: 2,
                                    boxShadow: 2,
                                    borderRadius: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start'
                                }}
                            >
                                <Typography variant="h6" sx={{ marginBottom: 1 }}>
                                    Teacher: {session.teacher_name}
                                </Typography>
                                <Typography variant="body1" sx={{ marginBottom: 1 }}>
                                    Session ID: {session.session_id}
                                </Typography>
                                <Typography variant="body1" sx={{ marginBottom: 1 }}>
                                    Type: {session.type}
                                </Typography>
                                <Typography variant="body1" sx={{ marginBottom: 1 }}>
                                    Status: {session.status}
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: 2 }}>
                                    Created At: {new Date(session.created_at).toLocaleString()}
                                </Typography>
                                <Button
                                    variant="contained"
                                    sx={{ backgroundColor: '#4caf50' }}
                                    onClick={() => handlePaymentCompletedClick(session.session_id)}
                                    disabled={session.status === 'payment completed'}
                                >
                                    Payment Completed
                                </Button>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Confirmation Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleDialogClose}
            >
                <DialogTitle>Confirm Payment Completion</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to mark this session's payment as completed?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmPayment} color="primary" autoFocus>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CompletedSessions;
